/* ==========================================================================
   ELSENYA — cartes produit : galerie défilante, ajout rapide, progression
   JavaScript natif, sans dépendance. Compatible avec les composants Prestige
   (<product-card>, <product-form>, <scroll-carousel>).
   ========================================================================== */
(() => {
  if (window.__elsenyaCards) return;
  window.__elsenyaCards = true;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const whenParsed = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Galerie horizontale dans la carte                                  */
  /* ------------------------------------------------------------------ */
  class ElsenyaCardGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.ecg__viewport');
      this.slides = Array.from(this.querySelectorAll('.ecg__slide'));
      if (!this.viewport || this.slides.length < 2) return;

      this.prevButton = this.querySelector('[data-ecg-prev]');
      this.nextButton = this.querySelector('[data-ecg-next]');
      this.thumb = this.querySelector('[data-ecg-thumb]');
      this.index = 0;
      this.pending = false;
      this.imagesLoaded = false;
      this.startLeft = 0;
      this.drag = null;
      this.suppressClick = false;

      this.viewport.addEventListener('scroll', () => this.schedule(), { passive: true });
      this.viewport.addEventListener('keydown', (event) => this.onKeyDown(event));
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.viewport.addEventListener('pointerup', (event) => this.onPointerEnd(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerEnd(event));
      this.addEventListener('click', (event) => this.onClickCapture(event), true);

      this.prevButton?.addEventListener('click', (event) => this.step(event, -1));
      this.nextButton?.addEventListener('click', (event) => this.step(event, 1));

      ['pointerenter', 'touchstart', 'focusin'].forEach((type) => {
        this.addEventListener(type, () => this.loadImages(), { once: true, passive: true });
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
            idle(() => this.loadImages(1));
          }
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        this.resizeObserver.observe(this.viewport);
      }

      whenParsed(() => this.bindSwatches());
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    /* Charge les images (la 1re est déjà chargée par le thème). */
    loadImages(upTo = Infinity) {
      if (this.imagesLoaded && upTo === Infinity) return;
      this.slides.forEach((slide, slideIndex) => {
        if (slideIndex > upTo) return;
        slide.querySelectorAll('img[data-src]').forEach((image) => {
          image.src = image.dataset.src;
          if (image.dataset.srcset) image.srcset = image.dataset.srcset;
          image.removeAttribute('data-src');
          image.removeAttribute('data-srcset');
        });
      });
      if (upTo === Infinity) this.imagesLoaded = true;
    }

    schedule() {
      if (this.pending) return;
      this.pending = true;
      requestAnimationFrame(() => {
        this.pending = false;
        this.update();
      });
    }

    update() {
      const viewport = this.viewport;
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      const position = Math.abs(viewport.scrollLeft);
      const ratio = maxScroll > 0 ? clamp(position / maxScroll, 0, 1) : 0;
      const count = this.slides.length;
      this.index = Math.round(ratio * (count - 1));
      if (this.index > 0) this.loadImages();

      if (this.thumb) {
        this.thumb.style.width = `${100 / count}%`;
        this.thumb.style.transform = `translateX(${ratio * (count - 1) * 100 * (document.dir === 'rtl' ? -1 : 1)}%)`;
      }

      this.prevButton?.setAttribute('aria-disabled', String(this.index <= 0));
      this.nextButton?.setAttribute('aria-disabled', String(this.index >= count - 1));

      this.slides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === this.index;
        slide.setAttribute('aria-hidden', String(!isCurrent));
        slide.tabIndex = isCurrent ? 0 : -1;
      });
    }

    scrollToIndex(targetIndex) {
      const index = clamp(targetIndex, 0, this.slides.length - 1);
      this.loadImages();
      this.viewport.scrollTo({
        left: this.slides[index].offsetLeft * (document.dir === 'rtl' ? -1 : 1),
        behavior: scrollBehavior()
      });
    }

    step(event, direction) {
      event.preventDefault();
      event.stopPropagation();
      if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
      this.scrollToIndex(this.index + direction);
    }

    onKeyDown(event) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const forward = (event.key === 'ArrowRight') !== (document.dir === 'rtl');
        event.preventDefault();
        this.scrollToIndex(this.index + (forward ? 1 : -1));
      }
    }

    /* Glisser à la souris (le tactile utilise le défilement natif) */
    onPointerDown(event) {
      this.startLeft = this.viewport.scrollLeft;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.drag = { x: event.clientX, left: this.viewport.scrollLeft, active: false };
    }

    onPointerMove(event) {
      if (!this.drag) return;
      const deltaX = event.clientX - this.drag.x;
      if (!this.drag.active && Math.abs(deltaX) > 6) {
        this.drag.active = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
      }
      if (this.drag.active) this.viewport.scrollLeft = this.drag.left - deltaX;
    }

    onPointerEnd(event) {
      if (!this.drag) return;
      const wasDragging = this.drag.active;
      this.drag = null;
      this.viewport.classList.remove('is-dragging');
      if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      if (wasDragging) {
        this.suppressClick = true;
        setTimeout(() => { this.suppressClick = false; }, 80);
        this.scrollToIndex(Math.round(Math.abs(this.viewport.scrollLeft) / this.viewport.clientWidth));
      }
    }

    /* Un geste de défilement n'ouvre jamais la fiche produit. */
    onClickCapture(event) {
      const scrolled = event.detail > 0 && Math.abs(this.viewport.scrollLeft - this.startLeft) > 4;
      if (this.suppressClick || scrolled) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    /* Synchronise les pastilles de couleur de la carte avec la galerie. */
    bindSwatches() {
      const card = this.closest('product-card');
      if (!card) return;
      card.querySelectorAll('.product-card__info input[type="radio"]').forEach((input) => {
        const raw = input.getAttribute('data-variant-media');
        if (raw) {
          try { input.dataset.ecgMedia = String(JSON.parse(raw).id); } catch (error) { /* ignore */ }
          /* On retire les attributs pour que le thème ne remplace pas l'image du 1er plan. */
          input.removeAttribute('data-variant-media');
          input.removeAttribute('data-variant-secondary-media');
        }
      });
      card.addEventListener('change', (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.dataset.ecgMedia) this.showMedia(target.dataset.ecgMedia);
      });
    }

    showMedia(mediaId) {
      const slideIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (slideIndex >= 0) this.scrollToIndex(slideIndex);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Ajout rapide : bouton + sélecteur de variante                      */
  /* ------------------------------------------------------------------ */
  class ElsenyaQuickAdd extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-ecq-trigger]');
      this.panel = this.querySelector('[data-ecq-panel]');
      this.form = this.querySelector('form');
      this.labels = {
        add: this.dataset.labelAdd || 'Add to cart',
        added: this.dataset.labelAdded || 'Added',
        pick: this.dataset.labelPick || 'Select',
        soldOut: this.dataset.labelSoldOut || 'Sold out'
      };

      /* Mode direct : un seul choix possible, le bouton ajoute tout de suite. */
      if (!this.panel) {
        this.form?.addEventListener('variant:add', () => this.flashAdded(this.trigger));
        return;
      }

      const data = this.querySelector('[data-ecq-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.optionCount = this.variants.length ? this.variants[0].options.length : 0;
      this.selected = new Array(this.optionCount).fill(null);
      this.chips = Array.from(this.querySelectorAll('.ecq__chip'));
      this.idInput = this.form.querySelector('input[name="id"]');
      this.submitButton = this.querySelector('[data-ecq-submit]');
      this.priceEl = this.querySelector('[data-ecq-price]');
      this.errorEl = this.querySelector('[data-ecq-error]');
      this.optionNames = Array.from(this.querySelectorAll('.ecq__group')).map((group) => group.dataset.optionName || '');
      this.outsideHandler = (event) => { if (!this.contains(event.target)) this.close(false); };

      this.trigger.addEventListener('click', () => (this.classList.contains('is-open') ? this.close(true) : this.open()));
      this.querySelector('[data-ecq-close]')?.addEventListener('click', () => this.close(true));
      this.panel.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.stopPropagation(); this.close(true); } });
      this.chips.forEach((chip) => chip.addEventListener('click', () => this.choose(chip)));
      this.form.addEventListener('submit', (event) => this.onSubmit(event), true);
      this.form.addEventListener('variant:add', () => this.onAdded());
      this.form.addEventListener('cart:error', (event) => this.showError(event.detail && event.detail.error));

      /* Les options à valeur unique sont sélectionnées d'office. */
      this.optionGroups().forEach((group, optionIndex) => {
        const chips = group.querySelectorAll('.ecq__chip');
        if (chips.length === 1) this.selected[optionIndex] = chips[0].dataset.value;
      });
      this.refresh();
    }

    optionGroups() {
      return Array.from(this.querySelectorAll('.ecq__group'));
    }

    open() {
      document.dispatchEvent(new CustomEvent('ecq:open', { detail: { source: this } }));
      this.prefillColorFromSwatch();
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.inert = false;
      document.addEventListener('pointerdown', this.outsideHandler, true);
      this.hideError();
      this.refresh();
      const firstFree = this.chips.find((chip) => chip.getAttribute('aria-pressed') !== 'true' && !chip.classList.contains('is-unavailable')) || this.chips[0];
      setTimeout(() => firstFree?.focus({ preventScroll: true }), prefersReducedMotion() ? 0 : 220);
    }

    close(returnFocus) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.inert = true;
      document.removeEventListener('pointerdown', this.outsideHandler, true);
      if (returnFocus) this.trigger.focus({ preventScroll: true });
    }

    /* Reprend la couleur déjà choisie via les pastilles de la carte. */
    prefillColorFromSwatch() {
      const card = this.closest('product-card');
      const checked = card?.querySelector('.product-card__info input[type="radio"]:checked');
      const label = checked?.labels?.[0]?.textContent.trim();
      if (!label) return;
      this.optionGroups().forEach((group, optionIndex) => {
        if (group.dataset.isColor !== 'true') return;
        const chip = Array.from(group.querySelectorAll('.ecq__chip')).find((candidate) => candidate.dataset.value === label);
        if (chip && this.selected[optionIndex] !== label) this.selected[optionIndex] = label;
      });
    }

    choose(chip) {
      if (chip.classList.contains('is-unavailable')) return;
      const optionIndex = Number(chip.dataset.option);
      this.selected[optionIndex] = chip.dataset.value;
      this.hideError();

      /* Si ce choix rend une sélection précédente impossible, on la retire. */
      this.selected.forEach((value, otherIndex) => {
        if (otherIndex === optionIndex || value === null) return;
        const stillPossible = this.variants.some((variant) => variant.available
          && variant.options[optionIndex] === this.selected[optionIndex]
          && variant.options[otherIndex] === value);
        if (!stillPossible) this.selected[otherIndex] = null;
      });

      this.refresh();

      /* Couleur : on synchronise la pastille et la galerie de la carte. */
      if (chip.closest('.ecq__group')?.dataset.isColor === 'true') this.syncSwatch(chip.dataset.value);
    }

    syncSwatch(colorName) {
      const card = this.closest('product-card');
      const input = Array.from(card?.querySelectorAll('.product-card__info input[type="radio"]') || [])
        .find((candidate) => candidate.labels?.[0]?.textContent.trim() === colorName);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (input) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        /* Pas de pastille : on cherche l'image de la première variante de cette couleur. */
        const colorIndex = this.optionGroups().findIndex((group) => group.dataset.isColor === 'true');
        const variant = this.variants.find((candidate) => candidate.options[colorIndex] === colorName && candidate.media);
        if (variant) card?.querySelector('elsenya-card-gallery')?.showMedia(variant.media);
      }
    }

    currentVariant() {
      if (this.selected.some((value) => value === null)) return null;
      return this.variants.find((variant) => variant.options.every((value, optionIndex) => value === this.selected[optionIndex])) || null;
    }

    refresh() {
      this.chips.forEach((chip) => {
        const optionIndex = Number(chip.dataset.option);
        const value = chip.dataset.value;
        const possible = this.variants.filter((variant) => variant.options[optionIndex] === value
          && this.selected.every((selectedValue, otherIndex) => otherIndex === optionIndex || selectedValue === null || variant.options[otherIndex] === selectedValue));
        const available = possible.some((variant) => variant.available);
        chip.classList.toggle('is-unavailable', !available);
        chip.setAttribute('aria-pressed', String(this.selected[optionIndex] === value));
        if (!available) chip.title = this.labels.soldOut; else chip.removeAttribute('title');
      });

      this.optionGroups().forEach((group, optionIndex) => {
        const selectedLabel = group.querySelector('[data-ecq-selected]');
        if (selectedLabel) selectedLabel.textContent = this.selected[optionIndex] || '';
      });

      const variant = this.currentVariant();
      const missingIndex = this.selected.findIndex((value) => value === null);

      if (variant) {
        this.idInput.value = variant.id;
        this.idInput.disabled = false;
        if (this.priceEl) {
          this.priceEl.innerHTML = '';
          this.priceEl.append(document.createTextNode(variant.price));
          if (variant.compare) {
            const compare = document.createElement('span');
            compare.className = 'ecq__compare';
            compare.textContent = variant.compare;
            this.priceEl.append(compare);
          }
        }
      } else {
        this.idInput.value = '';
      }

      const ready = Boolean(variant && variant.available);
      this.submitButton.setAttribute('aria-disabled', String(!ready));
      const text = this.submitButton.querySelector('[data-ecq-submit-text]');
      if (text) {
        if (ready) text.textContent = this.labels.add;
        else if (variant) text.textContent = this.labels.soldOut;
        else text.textContent = `${this.labels.pick} ${this.optionNames[missingIndex] || ''}`.trim();
      }
    }

    onSubmit(event) {
      if (this.submitButton.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        const missing = this.optionGroups()[this.selected.findIndex((value) => value === null)];
        missing?.querySelector('.ecq__chip:not(.is-unavailable)')?.focus();
      }
    }

    onAdded() {
      this.flashAdded(this.submitButton);
      setTimeout(() => this.close(false), prefersReducedMotion() ? 0 : 1100);
    }

    flashAdded(button) {
      if (!button) return;
      const text = button.querySelector('[data-ecq-submit-text], span');
      const previous = text ? text.textContent : '';
      button.classList.add('is-added');
      if (text) text.textContent = this.labels.added;
      setTimeout(() => {
        button.classList.remove('is-added');
        if (text) text.textContent = previous || this.labels.add;
        if (this.panel) this.refresh();
      }, 2200);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    hideError() {
      this.showError('');
    }
  }

  /* Un seul panneau ouvert à la fois. */
  document.addEventListener('ecq:open', (event) => {
    document.querySelectorAll('elsenya-quick-add.is-open').forEach((element) => {
      if (element !== event.detail.source) element.close(false);
    });
  });

  /* ------------------------------------------------------------------ */
  /* Barre de progression fine pour les carrousels horizontaux          */
  /* ------------------------------------------------------------------ */
  class ElsenyaScrollProgress extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.thumb = document.createElement('span');
      this.thumb.className = 'esp__thumb';
      this.append(this.thumb);
      this.setAttribute('aria-hidden', 'true');
      this.pending = false;
      whenParsed(() => {
        this.target = document.getElementById(this.getAttribute('aria-controls'));
        if (!this.target) return;
        this.target.addEventListener('scroll', () => this.schedule(), { passive: true });
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.schedule());
          this.resizeObserver.observe(this.target);
        }
        window.addEventListener('resize', () => this.schedule(), { passive: true });
        this.update();
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    schedule() {
      if (this.pending) return;
      this.pending = true;
      requestAnimationFrame(() => { this.pending = false; this.update(); });
    }

    update() {
      const target = this.target;
      if (!target) return;
      const maxScroll = target.scrollWidth - target.clientWidth;
      this.hidden = maxScroll <= 2;
      if (this.hidden) return;
      const widthRatio = clamp(target.clientWidth / target.scrollWidth, 0.08, 1);
      const ratio = clamp(Math.abs(target.scrollLeft) / maxScroll, 0, 1);
      const widthPercent = widthRatio * 100;
      const translate = (ratio * (100 - widthPercent)) / widthPercent * 100;
      this.thumb.style.width = `${widthPercent}%`;
      this.thumb.style.transform = `translateX(${translate * (document.dir === 'rtl' ? -1 : 1)}%)`;
    }
  }

  const define = (name, constructor) => {
    if (!window.customElements.get(name)) window.customElements.define(name, constructor);
  };

  define('elsenya-card-gallery', ElsenyaCardGallery);
  define('elsenya-quick-add', ElsenyaQuickAdd);
  define('elsenya-scroll-progress', ElsenyaScrollProgress);
})();
